import React, { FC, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Services
import { deployConfiguration, saveFullConfiguration, WizardData } from './services';

// Constants
import { LANGUAGE_OPTIONS } from './constants';

// Types
import type { ProfileType } from './types';

// Use language options from constants
const languageOptions = LANGUAGE_OPTIONS;

// User details interface
interface UserDetails {
  fullName: string;
  age: string;
  language: string;
}
// Canonical 7 launch layout IDs — kept in sync with Android LayoutTypes.kt
type TemplateId = 'pa_1' | 'pa_2' | 'pb_2' | 'pc_2' | 'cb_1' | 'vi_1' | 'vi_2';

interface ProfileOption {
  id: ProfileType;
  title: string;
  description: string;
  icon: string;
  bgColor: string;
  iconColor: string;
  templateId: TemplateId;
}

interface BasicFeature {
  id: string;
  name: string;
  description: string;
  recommended: ProfileType[];
  enabled: boolean;
}

interface PremiumFeature {
  id: string;
  name: string;
  description: string;
  price: string;
  recommended: ProfileType[];
  enabled: boolean;
  borderColor: string;
}

interface AccessibilitySettings {
  textSize: number;
  highContrast: boolean;
  voiceCommands: boolean;
}

// Profile-specific default accessibility settings
const profileAccessibilityDefaults: Record<ProfileType, AccessibilitySettings> = {
  cognitive: { textSize: 125, highContrast: false, voiceCommands: false },
  physical: { textSize: 150, highContrast: false, voiceCommands: true },
  visual: { textSize: 200, highContrast: true, voiceCommands: true },
  custom: { textSize: 100, highContrast: false, voiceCommands: false },
};

// Profile-specific default enabled features
const profileFeatureDefaults: Record<ProfileType, string[]> = {
  cognitive: ['emergency', 'contacts', 'quickContacts', 'simplifiedUI', 'endCall'],
  physical: ['emergency', 'contacts', 'voiceAssistant', 'endCall', 'largeButtons', 'simplifiedUI'],
  visual: ['emergency', 'contacts', 'voiceAssistant', 'endCall', 'quickContacts', 'largeButtons'],
  custom: ['emergency', 'contacts'],
};

const profiles: ProfileOption[] = [
  {
    id: 'cognitive',
    title: 'Memory & Confusion',
    description: 'Memory issues, confusion, or Alzheimer\'s. Requires simplified choices and clear visual cues.',
    icon: 'psychology_alt',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20 group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30',
    iconColor: 'text-orange-600 dark:text-orange-400',
    templateId: 'cb_1',
  },
  {
    id: 'physical',
    title: 'Physical Impairment',
    description: 'Tremors, arthritis, limited mobility, or Parkinson\'s. Needs larger buttons and stable interfaces.',
    icon: 'touch_app',
    bgColor: 'bg-green-50 dark:bg-green-900/20 group-hover:bg-green-100 dark:group-hover:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
    templateId: 'pa_1',
  },
  {
    id: 'visual',
    title: 'Visual Limitation',
    description: 'Low vision, macular degeneration, or cataracts. Requires high contrast and large text.',
    icon: 'visibility',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    templateId: 'vi_1',
  },
  {
    id: 'custom',
    title: 'Manual Setup',
    description: 'Standard settings allowing manual feature selection.',
    icon: 'tune',
    bgColor: 'bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700',
    iconColor: 'text-gray-500 dark:text-gray-400',
    templateId: 'pc_2',
  },
];

// Initial basic features with profile recommendations
const initialBasicFeatures: BasicFeature[] = [
  { id: 'emergency', name: 'One-Touch Emergency', description: 'Large red 911 button always visible at the top of the screen. Single tap calls emergency services.', recommended: ['cognitive', 'physical', 'visual'], enabled: false },
  { id: 'contacts', name: 'Contact List Access', description: 'Access to full contact list with large, easy-to-read buttons. Perfect for staying connected.', recommended: ['cognitive', 'physical', 'visual'], enabled: false },
  { id: 'voiceAssistant', name: 'Voice Assistant', description: 'Say "Call Marie" to make calls. Supports French and English. No typing required.', recommended: ['physical', 'visual'], enabled: false },
  { id: 'endCall', name: 'End Call Button', description: 'Large, prominent button to easily end calls or cancel operations. Essential for control.', recommended: ['cognitive', 'physical', 'visual'], enabled: false },
  { id: 'numberKeypad', name: 'Number Keypad', description: 'Traditional phone keypad for dialing any number. Includes number display area.', recommended: ['cognitive'], enabled: false },
  { id: 'quickContacts', name: 'Quick Contact Buttons', description: 'Direct call buttons for family members (Martine, Thomas, etc.). No menu navigation needed.', recommended: ['cognitive', 'visual'], enabled: false },
  { id: 'largeButtons', name: 'Extra Large Buttons', description: 'Buttons 200% larger than normal size for easier touching. Reduces accidental presses.', recommended: ['physical', 'visual'], enabled: false },
  { id: 'simplifiedUI', name: 'Simplified Interface', description: 'Shows only essential buttons. Removes confusing options and complex layouts.', recommended: ['cognitive', 'physical'], enabled: false },
];

// Initial premium features with profile recommendations
const initialPremiumFeatures: PremiumFeature[] = [
  { id: 'medication', name: 'Medication Reminders', description: 'Voice alerts for medications. Family gets notification if dose is missed.', price: '+$0.99/mo', recommended: ['cognitive'], enabled: false, borderColor: 'border-green-200 dark:border-green-800' },
  { id: 'gps', name: 'GPS Location', description: "Family can see location if user doesn't answer. Peace of mind for wandering.", price: '+$1.49/mo', recommended: ['cognitive'], enabled: false, borderColor: 'border-yellow-200 dark:border-yellow-800' },
  { id: 'phoneDropAlert', name: 'Phone Drop Alert', description: 'Detects sudden phone drops. Alerts family if phone may have been dropped during an incident.', price: '+$1.99/mo', recommended: ['physical'], enabled: false, borderColor: 'border-orange-200 dark:border-orange-800' },
  { id: 'videoCalls', name: 'Simple Video Calls', description: 'One-touch video with family. Automatically answers from approved contacts.', price: '+$0.79/mo', recommended: ['cognitive', 'visual'], enabled: false, borderColor: 'border-blue-200 dark:border-blue-800' },
  { id: 'highContrastPremium', name: 'High Contrast Mode', description: 'Black background with white text. Maximum visibility for low vision.', price: '+$0.49/mo', recommended: ['visual'], enabled: false, borderColor: 'border-purple-200 dark:border-purple-800' },
];

// Success data interface for the success modal
interface DeploySuccessData {
  deviceId: string;
  profile: ProfileType;
  templateId: string;
  features: string[];
  monthlyCost: string;
}

const CaregiverPortal: FC = () => {
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [selectedProfile, setSelectedProfile] = useState<ProfileType>('cognitive');
  const [basicFeatures, setBasicFeatures] = useState<BasicFeature[]>(initialBasicFeatures);
  const [premiumFeatures, setPremiumFeatures] = useState<PremiumFeature[]>(initialPremiumFeatures);
  const [accessibility, setAccessibility] = useState<AccessibilitySettings>(profileAccessibilityDefaults.cognitive);
  const [isDeploying, setIsDeploying] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<DeploySuccessData | null>(null);
  const [deviceId, setDeviceId] = useState('');
  const [deployError, setDeployError] = useState('');
  const navigate = useNavigate();

  // User details state
  const [userDetails, setUserDetails] = useState<UserDetails>({
    fullName: '',
    age: '',
    language: 'en-US',
  });
  const [languageSearch, setLanguageSearch] = useState('');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  // Filter languages based on search
  const filteredLanguages = useMemo(() => {
    if (!languageSearch) return languageOptions;
    const search = languageSearch.toLowerCase();
    return languageOptions.filter(
      lang => lang.label.toLowerCase().includes(search) || lang.code.toLowerCase().includes(search)
    );
  }, [languageSearch]);

  // Get selected language label
  const selectedLanguageLabel = languageOptions.find(l => l.code === userDetails.language)?.label || 'Select Language';

  // Get current template ID based on selected profile
  const getTemplateId = (): TemplateId => {
    const profile = profiles.find(p => p.id === selectedProfile);
    return profile?.templateId || 'pa_1';
  };

  // Calculate monthly cost
  const calculateMonthlyCost = () => {
    let cost = 4.99; // Base price
    premiumFeatures.forEach((feature) => {
      if (feature.enabled) {
        const price = parseFloat(feature.price.replace('+$', '').replace('/mo', ''));
        cost += price;
      }
    });
    return cost.toFixed(2);
  };

  // Apply profile defaults when moving to step 2
  const applyProfileDefaults = (profile: ProfileType) => {
    // Set accessibility defaults
    setAccessibility(profileAccessibilityDefaults[profile]);

    // Set feature defaults
    const defaultFeatureIds = profileFeatureDefaults[profile];

    setBasicFeatures(prev => prev.map(f => ({
      ...f,
      enabled: defaultFeatureIds.includes(f.id)
    })));

    // For visual profile, auto-enable high contrast premium
    if (profile === 'visual') {
      setPremiumFeatures(prev => prev.map(f => ({
        ...f,
        enabled: f.id === 'highContrastPremium'
      })));
    } else {
      setPremiumFeatures(prev => prev.map(f => ({ ...f, enabled: false })));
    }
  };

  const handleGoBack = () => {
    if (currentStep === 3) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      navigate('/');
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Validate user details before proceeding
      if (!userDetails.fullName.trim()) {
        alert('Please enter a full name');
        return;
      }
      if (!userDetails.age || parseInt(userDetails.age) < 1 || parseInt(userDetails.age) > 150) {
        alert('Please enter a valid age');
        return;
      }
      applyProfileDefaults(selectedProfile);
      setCurrentStep(3);
    }
  };

  const toggleBasicFeature = (featureId: string) => {
    setBasicFeatures((prev) =>
      prev.map((f) => (f.id === featureId ? { ...f, enabled: !f.enabled } : f))
    );
  };

  const togglePremiumFeature = (featureId: string) => {
    setPremiumFeatures((prev) =>
      prev.map((f) => (f.id === featureId ? { ...f, enabled: !f.enabled } : f))
    );
  };

  // Deploy configuration and save to Supabase
  const handleDeployClick = async () => {
    setIsDeploying(true);
    setDeployError('');

    const enabledBasicFeatures = basicFeatures.filter(f => f.enabled).map(f => f.id);
    const enabledPremiumFeatures = premiumFeatures.filter(f => f.enabled).map(f => f.id);
    const allEnabledFeatures = [...enabledBasicFeatures, ...enabledPremiumFeatures];

    // Generate activation code
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let activationCode = 'AID-';
    for (let i = 0; i < 4; i++) {
      activationCode += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    const configData = {
      profile: selectedProfile,
      templateId: getTemplateId(),
      features: allEnabledFeatures,
      accessibility: {
        textSize: accessibility.textSize,
        highContrast: accessibility.highContrast,
        voiceCommands: accessibility.voiceCommands,
      }
    };

    try {
      // NOTE: CaregiverPortal is legacy B2B code. The /add-senior-configuration
      // route is gated behind FEATURES.B2B_MULTI_SENIOR, so in B2C mode this
      // code path is unreachable. caregiverId is left empty to satisfy the type;
      // if B2B mode is re-enabled, this needs a real auth user id.
      const wizardData: WizardData = {
        caregiverId: '',
        seniorName: userDetails.fullName.trim(),
        profileType: selectedProfile === 'custom' ? 'cognitive' : selectedProfile as 'cognitive' | 'physical' | 'visual',
        severity: 'moderate', // Default severity for legacy flow
        assessmentScore: 2.5,
        assessmentAnswers: {},
        planTier: 'plus', // Default plan for legacy flow
        planPrice: parseFloat(calculateMonthlyCost()),
        layoutId: getTemplateId(),
        contacts: [],
        activationCode,
      };

      // Save to new Supabase schema
      const result = await saveFullConfiguration(wizardData);

      // Generate device ID from config
      const generatedDeviceId = result.configuration.id || `device-${Date.now()}`;

      // Also deploy to the backend server (optional - for real-time sync)
      try {
        await deployConfiguration(generatedDeviceId, configData);
      } catch (backendError) {
        console.warn('Backend deployment failed, but Supabase save succeeded:', backendError);
      }

      // Store success data and show success modal
      setSuccessData({
        deviceId: activationCode,
        profile: selectedProfile,
        templateId: getTemplateId(),
        features: allEnabledFeatures,
        monthlyCost: calculateMonthlyCost(),
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Deploy error:', error);
      setDeployError('Failed to save configuration. Please try again.');
    } finally {
      setIsDeploying(false);
    }
  };

  /* COMMENTED OUT - Modal-based deploy
  // Opens the deploy modal
  const handleDeployClickWithModal = () => {
    setDeployError('');
    setShowDeployModal(true);
  };

  // Actual deployment logic with modal
  const handleDeploy = async () => {
    if (!deviceId.trim()) {
      setDeployError('Please enter a Device ID');
      return;
    }

    setIsDeploying(true);
    setDeployError('');

    const enabledBasicFeatures = basicFeatures.filter(f => f.enabled).map(f => f.id);
    const enabledPremiumFeatures = premiumFeatures.filter(f => f.enabled).map(f => f.id);
    const allEnabledFeatures = [...enabledBasicFeatures, ...enabledPremiumFeatures];

    const configData = {
      profile: selectedProfile,
      templateId: getTemplateId(),
      features: allEnabledFeatures,
      accessibility: {
        textSize: accessibility.textSize,
        highContrast: accessibility.highContrast,
        voiceCommands: accessibility.voiceCommands,
      }
    };

    try {
      await deployConfiguration(deviceId.trim(), configData);
      // Store success data and show success modal
      setSuccessData({
        deviceId: deviceId.trim(),
        profile: selectedProfile,
        templateId: getTemplateId(),
        features: allEnabledFeatures,
        monthlyCost: calculateMonthlyCost(),
      });
      setShowDeployModal(false);
      setDeviceId('');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Deploy error:', error);
      setDeployError('Failed to deploy. Please check your Device ID and try again.');
    } finally {
      setIsDeploying(false);
    }
  };
  */

  // Check if feature is recommended for current profile
  const isRecommended = (feature: BasicFeature | PremiumFeature) => {
    return feature.recommended.includes(selectedProfile);
  };

  // Render Profile Selection (Step 1)
  const renderProfileSelection = () => (
    <div className="max-w-6xl mx-auto">
      <div className="mb-10">
        <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-gray-900 dark:text-white mb-4">
          Who is this device for?
        </h1>
        <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl leading-relaxed font-medium">
          Select the profile that best matches the senior's needs. This will set up the easiest way for them to
          use the phone.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {profiles.map((profile) => {
          const isSelected = selectedProfile === profile.id;
          return (
            <label
              key={profile.id}
              className={`group relative flex flex-col bg-white dark:bg-surface-dark rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden ring-offset-4 focus-within:ring-4 ring-primary/30 h-full ${
                isSelected
                  ? 'border-[6px] border-primary shadow-xl transform scale-[1.01]'
                  : 'border-2 border-gray-200 dark:border-gray-700 hover:border-primary'
              }`}
            >
              <input
                className="peer sr-only"
                name="profile"
                type="radio"
                value={profile.id}
                checked={isSelected}
                onChange={() => setSelectedProfile(profile.id)}
              />

              <div
                className={`absolute top-6 right-6 bg-primary text-white rounded-full p-2 transition-all transform z-10 shadow-lg ${
                  isSelected ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                }`}
              >
                <span className="material-symbols-outlined text-4xl font-bold">check</span>
              </div>

              <div className={`h-52 w-full flex items-center justify-center transition-colors ${profile.bgColor}`}>
                <span
                  className={`material-symbols-outlined text-8xl md:text-9xl group-hover:scale-110 transition-transform duration-300 ${profile.iconColor}`}
                >
                  {profile.icon}
                </span>
              </div>

              <div className="p-8 flex flex-col flex-1">
                <h3
                  className={`text-2xl font-bold mb-4 transition-colors ${
                    isSelected ? 'text-primary' : 'text-gray-900 dark:text-white group-hover:text-primary'
                  }`}
                >
                  {profile.title}
                </h3>
                <p
                  className={`text-lg leading-relaxed ${
                    isSelected
                      ? 'text-gray-800 dark:text-gray-100 font-bold'
                      : 'text-gray-700 dark:text-gray-300 font-medium'
                  }`}
                >
                  {profile.description}
                </p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );

  // Render User Details (Step 2)
  const renderUserDetails = () => (
    <div className="max-w-2xl mx-auto">
      <div className="mb-10">
        <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-gray-900 dark:text-white mb-4">
          User Details
        </h1>
        <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl leading-relaxed font-medium">
          Enter the details of the person who will use this device.
        </p>
      </div>

      <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm p-8 space-y-8">
        {/* Full Name */}
        <div>
          <label htmlFor="fullName" className="block text-lg font-bold text-gray-900 dark:text-white mb-3">
            Full Name
          </label>
          <input
            type="text"
            id="fullName"
            value={userDetails.fullName}
            onChange={(e) => setUserDetails(prev => ({ ...prev, fullName: e.target.value }))}
            placeholder="Enter full name"
            className="w-full px-5 py-4 text-lg rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
          />
        </div>

        {/* Age */}
        <div>
          <label htmlFor="age" className="block text-lg font-bold text-gray-900 dark:text-white mb-3">
            Age
          </label>
          <input
            type="number"
            id="age"
            value={userDetails.age}
            onChange={(e) => setUserDetails(prev => ({ ...prev, age: e.target.value }))}
            placeholder="Enter age"
            min="1"
            max="150"
            className="w-full px-5 py-4 text-lg rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
          />
        </div>

        {/* Language Dropdown with Search */}
        <div>
          <label className="block text-lg font-bold text-gray-900 dark:text-white mb-3">
            Language
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
              className="w-full px-5 py-4 text-lg rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all flex items-center justify-between"
            >
              <span className="flex items-center gap-3">
                <span className="text-2xl">{languageOptions.find(l => l.code === userDetails.language)?.flag}</span>
                <span>{selectedLanguageLabel}</span>
              </span>
              <span className={`material-symbols-outlined text-gray-400 transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`}>
                expand_more
              </span>
            </button>

            {isLanguageDropdownOpen && (
              <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden">
                {/* Search Input */}
                <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      search
                    </span>
                    <input
                      type="text"
                      value={languageSearch}
                      onChange={(e) => setLanguageSearch(e.target.value)}
                      placeholder="Search languages..."
                      className="w-full pl-10 pr-4 py-3 text-base rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Language List */}
                <div className="max-h-64 overflow-y-auto">
                  {filteredLanguages.length === 0 ? (
                    <div className="px-5 py-4 text-gray-500 dark:text-gray-400 text-center">
                      No languages found
                    </div>
                  ) : (
                    filteredLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => {
                          setUserDetails(prev => ({ ...prev, language: lang.code }));
                          setIsLanguageDropdownOpen(false);
                          setLanguageSearch('');
                        }}
                        className={`w-full px-5 py-3 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          userDetails.language === lang.code ? 'bg-primary/10 text-primary' : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        <span className="text-2xl">{lang.flag}</span>
                        <span className="font-medium">{lang.label}</span>
                        {userDetails.language === lang.code && (
                          <span className="material-symbols-outlined ml-auto text-primary">check</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            This sets the device language and voice assistant language.
          </p>
        </div>

        {/* Selected Profile Summary */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${profiles.find(p => p.id === selectedProfile)?.bgColor}`}>
              <span className={`material-symbols-outlined text-3xl ${profiles.find(p => p.id === selectedProfile)?.iconColor}`}>
                {profiles.find(p => p.id === selectedProfile)?.icon}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Selected Profile</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {profiles.find(p => p.id === selectedProfile)?.title}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Live Preview based on profile
  const renderLivePreview = () => {
    // Profile-specific preview configurations
    const previewConfigs = {
      cognitive: {
        bgColor: accessibility.highContrast ? 'bg-gray-900' : 'bg-amber-50',
        textColor: accessibility.highContrast ? 'text-white' : 'text-gray-900',
        subtitle: 'Photo Contacts Layout',
      },
      physical: {
        bgColor: accessibility.highContrast ? 'bg-gray-900' : 'bg-slate-100',
        textColor: accessibility.highContrast ? 'text-white' : 'text-gray-900',
        subtitle: '4-Button Simplified Layout',
      },
      visual: {
        bgColor: 'bg-black',
        textColor: 'text-white',
        subtitle: 'High Contrast Layout',
      },
      custom: {
        bgColor: accessibility.highContrast ? 'bg-gray-900' : 'bg-white',
        textColor: accessibility.highContrast ? 'text-white' : 'text-gray-900',
        subtitle: 'Standard Layout',
      },
    };

    const config = previewConfigs[selectedProfile];
    const isVisual = selectedProfile === 'visual' || accessibility.highContrast;

    return (
      <div className="w-[480px] hidden xl:flex flex-col items-center justify-center bg-gray-50 dark:bg-[#0c1616] border-l border-gray-200 dark:border-gray-800 relative shadow-inner">
        <div className="absolute top-10 flex items-center gap-2 text-gray-400 font-bold uppercase tracking-widest text-xs">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
          Live Preview
        </div>

        {/* Phone Mockup */}
        <div className="relative w-[320px] h-[640px] bg-black rounded-[3rem] shadow-2xl border-[10px] border-gray-900 overflow-hidden ring-4 ring-gray-200 dark:ring-gray-800">
          <div className={`h-full w-full ${config.bgColor} flex flex-col relative font-sans`}>
            {/* Status Bar */}
            <div className={`h-8 flex justify-between items-center px-5 pt-2 ${config.textColor}`}>
              <span className="text-xs font-medium">10:42</span>
              <div className="flex gap-1.5">
                <span className="material-symbols-outlined text-sm">wifi</span>
                <span className="material-symbols-outlined text-sm">battery_full</span>
              </div>
            </div>

            {/* Main Content - Profile Specific */}
            <div className="flex-1 p-4 flex flex-col overflow-hidden">
              {/* Emergency Button - All profiles */}
              {basicFeatures.find((f) => f.id === 'emergency')?.enabled && (
                <div className={`w-full h-14 ${isVisual ? 'bg-red-600 border-2 border-red-400' : 'bg-red-600'} rounded-2xl flex items-center justify-center gap-2 mb-4 shadow-lg`}>
                  <span className="material-symbols-outlined text-white text-2xl">emergency</span>
                  <span className="text-white font-bold" style={{ fontSize: `${accessibility.textSize * 0.1}px` }}>EMERGENCY</span>
                </div>
              )}

              {/* MEMORY & CONFUSION LAYOUT - Photo Contacts */}
              {selectedProfile === 'cognitive' && (
                <>
                  <div className="mb-4">
                    <p className={`font-light opacity-70 ${config.textColor}`} style={{ fontSize: `${accessibility.textSize * 0.1}px` }}>Hello,</p>
                    <h2 className={`font-bold ${config.textColor}`} style={{ fontSize: `${accessibility.textSize * 0.2}px` }}>Sarah</h2>
                  </div>

                  {basicFeatures.find((f) => f.id === 'quickContacts')?.enabled && (
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="aspect-square bg-gradient-to-br from-green-500 to-green-600 rounded-3xl flex flex-col items-center justify-center gap-2 shadow-lg p-4">
                        <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                          <span className="material-symbols-outlined text-white text-4xl">face</span>
                        </div>
                        <span className="text-white font-bold text-center" style={{ fontSize: `${accessibility.textSize * 0.09}px` }}>Call Mom</span>
                      </div>
                      <div className="aspect-square bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex flex-col items-center justify-center gap-2 shadow-lg p-4">
                        <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                          <span className="material-symbols-outlined text-white text-4xl">face</span>
                        </div>
                        <span className="text-white font-bold text-center" style={{ fontSize: `${accessibility.textSize * 0.09}px` }}>Call Dad</span>
                      </div>
                    </div>
                  )}

                  {basicFeatures.find((f) => f.id === 'numberKeypad')?.enabled && (
                    <div className={`flex-1 rounded-2xl p-3 ${isVisual ? 'bg-gray-800' : 'bg-white'} shadow-inner`}>
                      <div className="grid grid-cols-3 gap-2">
                        {[1,2,3,4,5,6,7,8,9,'*',0,'#'].map((num) => (
                          <div key={num} className={`aspect-square rounded-xl ${isVisual ? 'bg-gray-700' : 'bg-gray-100'} flex items-center justify-center`}>
                            <span className={`font-bold ${config.textColor}`} style={{ fontSize: `${accessibility.textSize * 0.14}px` }}>{num}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* PHYSICAL LAYOUT - 4 Large Buttons */}
              {selectedProfile === 'physical' && (
                <div className="flex-1 grid grid-cols-2 gap-4">
                  {basicFeatures.find((f) => f.id === 'contacts')?.enabled && (
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex flex-col items-center justify-center gap-3 shadow-lg">
                      <span className="material-symbols-outlined text-white" style={{ fontSize: `${accessibility.textSize * 0.35}px` }}>contacts</span>
                      <span className="text-white font-bold" style={{ fontSize: `${accessibility.textSize * 0.1}px` }}>Contacts</span>
                    </div>
                  )}
                  {basicFeatures.find((f) => f.id === 'voiceAssistant')?.enabled && (
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl flex flex-col items-center justify-center gap-3 shadow-lg">
                      <span className="material-symbols-outlined text-white" style={{ fontSize: `${accessibility.textSize * 0.35}px` }}>mic</span>
                      <span className="text-white font-bold" style={{ fontSize: `${accessibility.textSize * 0.1}px` }}>Voice</span>
                    </div>
                  )}
                  {basicFeatures.find((f) => f.id === 'endCall')?.enabled && (
                    <div className="col-span-2 h-24 bg-gradient-to-br from-gray-600 to-gray-700 rounded-3xl flex items-center justify-center gap-4 shadow-lg">
                      <span className="material-symbols-outlined text-white" style={{ fontSize: `${accessibility.textSize * 0.28}px` }}>call_end</span>
                      <span className="text-white font-bold" style={{ fontSize: `${accessibility.textSize * 0.12}px` }}>End Call</span>
                    </div>
                  )}
                </div>
              )}

              {/* VISUAL LAYOUT - High Contrast */}
              {selectedProfile === 'visual' && (
                <div className="flex-1 flex flex-col gap-4">
                  <div className="mb-2">
                    <p className="font-light text-yellow-400" style={{ fontSize: `${accessibility.textSize * 0.1}px` }}>Hello,</p>
                    <h2 className="font-black text-white" style={{ fontSize: `${accessibility.textSize * 0.24}px` }}>Sarah</h2>
                  </div>

                  {basicFeatures.find((f) => f.id === 'contacts')?.enabled && (
                    <div className="h-20 bg-yellow-500 border-4 border-yellow-300 rounded-2xl flex items-center px-6 gap-4 shadow-lg">
                      <span className="material-symbols-outlined text-black" style={{ fontSize: `${accessibility.textSize * 0.28}px` }}>contacts</span>
                      <span className="text-black font-black" style={{ fontSize: `${accessibility.textSize * 0.14}px` }}>CONTACTS</span>
                    </div>
                  )}

                  {basicFeatures.find((f) => f.id === 'voiceAssistant')?.enabled && (
                    <div className="h-20 bg-cyan-400 border-4 border-cyan-200 rounded-2xl flex items-center px-6 gap-4 shadow-lg">
                      <span className="material-symbols-outlined text-black" style={{ fontSize: `${accessibility.textSize * 0.28}px` }}>mic</span>
                      <span className="text-black font-black" style={{ fontSize: `${accessibility.textSize * 0.14}px` }}>VOICE HELP</span>
                    </div>
                  )}

                  {basicFeatures.find((f) => f.id === 'quickContacts')?.enabled && (
                    <div className="grid grid-cols-2 gap-3 flex-1">
                      <div className="bg-green-500 border-4 border-green-300 rounded-2xl flex flex-col items-center justify-center gap-2">
                        <span className="material-symbols-outlined text-black" style={{ fontSize: `${accessibility.textSize * 0.2}px` }}>call</span>
                        <span className="text-black font-black" style={{ fontSize: `${accessibility.textSize * 0.08}px` }}>MOM</span>
                      </div>
                      <div className="bg-blue-500 border-4 border-blue-300 rounded-2xl flex flex-col items-center justify-center gap-2">
                        <span className="material-symbols-outlined text-black" style={{ fontSize: `${accessibility.textSize * 0.2}px` }}>call</span>
                        <span className="text-black font-black" style={{ fontSize: `${accessibility.textSize * 0.08}px` }}>DAD</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* CUSTOM LAYOUT - Standard */}
              {selectedProfile === 'custom' && (
                <>
                  <div className="mb-4">
                    <p className={`font-light opacity-70 ${config.textColor}`} style={{ fontSize: `${accessibility.textSize * 0.1}px` }}>Hello,</p>
                    <h2 className={`font-bold ${config.textColor}`} style={{ fontSize: `${accessibility.textSize * 0.2}px` }}>Sarah</h2>
                  </div>

                  {basicFeatures.find((f) => f.id === 'contacts')?.enabled && (
                    <div className={`flex-1 rounded-2xl p-3 ${isVisual ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <p className={`text-xs font-bold mb-2 ${isVisual ? 'text-gray-400' : 'text-gray-500'}`}>CONTACTS</p>
                      <div className="space-y-2">
                        {['Mom', 'Dad', 'Doctor'].map((name) => (
                          <div key={name} className={`flex items-center gap-3 p-2 rounded-xl ${isVisual ? 'bg-gray-700' : 'bg-white'}`}>
                            <div className={`w-10 h-10 rounded-full ${isVisual ? 'bg-gray-600' : 'bg-gray-200'} flex items-center justify-center`}>
                              <span className={`material-symbols-outlined ${isVisual ? 'text-gray-400' : 'text-gray-500'}`}>person</span>
                            </div>
                            <span className={`font-bold ${config.textColor}`} style={{ fontSize: `${accessibility.textSize * 0.1}px` }}>{name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Navigation Bar */}
            <div className={`h-16 ${isVisual ? 'bg-gray-900 border-gray-700' : 'bg-gray-100 border-gray-200'} border-t flex justify-around items-center px-4`}>
              <span className={`material-symbols-outlined text-3xl ${isVisual ? 'text-gray-400' : 'text-gray-400'}`}>arrow_back</span>
              <div className={`w-12 h-12 rounded-full ${isVisual ? 'bg-gray-800' : 'bg-white'} flex items-center justify-center shadow`}>
                <span className={`material-symbols-outlined text-2xl ${isVisual ? 'text-white' : 'text-gray-900'}`}>home</span>
              </div>
              <span className={`material-symbols-outlined text-3xl ${isVisual ? 'text-gray-400' : 'text-gray-400'}`}>check_box_outline_blank</span>
            </div>
          </div>

          {/* Notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 h-6 w-28 bg-black rounded-b-xl z-20"></div>
        </div>

        {/* Device Info */}
        <div className="mt-6 px-8 text-center">
          <p className="text-sm font-medium text-gray-500">
            {config.subtitle}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Template: {getTemplateId()} • {accessibility.textSize}% Text
          </p>
        </div>
      </div>
    );
  };

  // Render Configuration & Deployment (Step 2)
  const renderConfiguration = () => (
    <div className="flex-1 flex h-full overflow-hidden">
      {/* Settings Panel */}
      <div className="flex-1 h-full flex flex-col overflow-hidden">
        {/* Sticky Header with Price */}
        <div className="sticky top-0 z-10 bg-white dark:bg-background-dark border-b border-gray-200 dark:border-gray-800 px-8 lg:px-12 py-6">
          <div className="max-w-2xl mx-auto flex items-start justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-black tracking-tight text-gray-900 dark:text-white mb-2">
                Select Features
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
                Choose the features for <span className="text-primary font-bold">{profiles.find(p => p.id === selectedProfile)?.title}</span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                Template: <span className="font-semibold text-primary">{getTemplateId()}</span>
              </p>
            </div>
            <div className="text-right bg-white dark:bg-surface-dark rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-800">
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Monthly cost</p>
              <p className="text-3xl font-black text-primary">${calculateMonthlyCost()}</p>
              <p className="text-xs text-gray-400">per device</p>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-8 lg:p-12 pt-6 sidebar-scroll">
          <div className="max-w-2xl mx-auto pb-32">
          {/* Basic Features Section */}
          <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm mb-6 overflow-hidden">
            <div className="flex items-center gap-3 p-6 bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
              <div className="p-2 bg-primary/10 rounded-lg">
                <span className="material-symbols-outlined text-primary text-xl">check_circle</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Basic Features (Included)</h3>
            </div>
            <div className="p-4 space-y-2">
              {basicFeatures.map((feature) => (
                <label
                  key={feature.id}
                  className={`flex items-start gap-4 p-4 rounded-xl cursor-pointer transition-colors ${
                    feature.enabled ? 'bg-primary/5' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={feature.enabled}
                    onChange={() => toggleBasicFeature(feature.id)}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary/20"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-base font-bold text-gray-900 dark:text-white">{feature.name}</span>
                      {isRecommended(feature) && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                          Recommended
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{feature.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Premium Features Section */}
          <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm mb-8 overflow-hidden">
            <div className="flex items-center gap-3 p-6 bg-orange-50/50 dark:bg-orange-900/10 border-b border-orange-100 dark:border-orange-900/20">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <span className="material-symbols-outlined text-orange-600 dark:text-orange-400 text-xl">star</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Premium Features</h3>
            </div>
            <div className="p-4 space-y-3">
              {premiumFeatures.map((feature) => (
                <label
                  key={feature.id}
                  className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    feature.enabled
                      ? 'border-primary bg-primary/5'
                      : `${feature.borderColor} bg-white dark:bg-surface-dark hover:border-primary/50`
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={feature.enabled}
                    onChange={() => togglePremiumFeature(feature.id)}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary/20"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-base font-bold text-gray-900 dark:text-white">{feature.name}</span>
                      <span className="px-2 py-0.5 text-xs font-bold text-primary bg-primary/10 rounded-full">
                        {feature.price}
                      </span>
                      {isRecommended(feature) && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-500 rounded-full">
                          Suggested
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{feature.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Accessibility Settings Section */}
          <details className="group bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm mb-8 overflow-hidden" open>
            <summary className="flex items-center justify-between p-6 cursor-pointer bg-gray-50/50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl text-purple-600 dark:text-purple-400">
                  <span className="material-symbols-outlined text-2xl">accessibility_new</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Accessibility Settings</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Pre-configured for {profiles.find(p => p.id === selectedProfile)?.title}</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-gray-400 text-3xl transition-transform duration-300 group-open:rotate-180">expand_more</span>
            </summary>
            <div className="p-6 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-8">
              {/* Text Size Slider */}
              <div>
                <div className="flex justify-between items-end mb-4">
                  <div className="flex flex-col">
                    <span className="text-lg font-bold text-gray-900 dark:text-white">Text Size</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Adjust global font scale</span>
                  </div>
                  <span className="text-xl font-bold text-primary">{accessibility.textSize}%</span>
                </div>
                <div className="relative w-full h-8 flex items-center">
                  <input
                    type="range"
                    min="100"
                    max="200"
                    step="25"
                    value={accessibility.textSize}
                    onChange={(e) => setAccessibility((prev) => ({ ...prev, textSize: Number(e.target.value) }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 z-10"
                  />
                  <div className="absolute w-full flex justify-between px-1 pointer-events-none">
                    {[100, 125, 150, 175, 200].map((v) => (
                      <div key={v} className="w-1 h-3 bg-gray-300 dark:bg-gray-600 rounded"></div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between mt-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  <span>Normal</span>
                  <span>Large</span>
                  <span>Max</span>
                </div>
              </div>

              <div className="h-px w-full bg-gray-100 dark:bg-gray-800"></div>

              {/* High Contrast Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-gray-900 dark:text-white">High Contrast Mode</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedProfile === 'visual' ? 'Enabled by default for Visual profile' : 'Black background with white text'}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accessibility.highContrast}
                    onChange={(e) => setAccessibility((prev) => ({ ...prev, highContrast: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-16 h-9 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-7 after:w-7 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Voice Commands Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-gray-900 dark:text-white">Voice Commands</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedProfile === 'physical' || selectedProfile === 'visual' ? 'Enabled by default for this profile' : 'Enable "Hey Google" assistance'}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accessibility.voiceCommands}
                    onChange={(e) => setAccessibility((prev) => ({ ...prev, voiceCommands: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-16 h-9 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-7 after:w-7 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </details>

          {/* Deploy Section */}
          <div className="bg-primary/5 dark:bg-primary/10 rounded-2xl p-8 border border-primary/20 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4">
              <span className="material-symbols-outlined text-4xl">rocket_launch</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Ready to Deploy?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-md mx-auto">
              This will save user <strong>{userDetails.fullName || 'Unknown'}</strong> and the <strong>{getTemplateId()}</strong> configuration to the database.
            </p>

            {/* User Summary */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-6 text-left">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">User</p>
                  <p className="text-gray-900 dark:text-white font-bold truncate">{userDetails.fullName || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">Age</p>
                  <p className="text-gray-900 dark:text-white font-bold">{userDetails.age || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">Language</p>
                  <p className="text-gray-900 dark:text-white font-bold">{languageOptions.find(l => l.code === userDetails.language)?.flag} {userDetails.language.split('-')[0].toUpperCase()}</p>
                </div>
              </div>
            </div>

            {deployError && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-left">
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <span className="material-symbols-outlined text-xl">error</span>
                  <p className="font-medium">{deployError}</p>
                </div>
              </div>
            )}

            <button
              onClick={handleDeployClick}
              disabled={isDeploying}
              className="w-full flex items-center justify-center h-16 bg-primary hover:bg-primary/90 text-white font-bold text-xl rounded-xl shadow-xl shadow-primary/20 transition-all transform hover:-translate-y-1 active:translate-y-0 focus:ring-4 ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isDeploying ? (
                <>
                  <span className="material-symbols-outlined animate-spin mr-3 text-2xl">progress_activity</span>
                  Saving to Database...
                </>
              ) : (
                <>
                  Deploy Configuration
                  <span className="material-symbols-outlined ml-3 text-2xl font-bold">arrow_forward</span>
                </>
              )}
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* Live Preview Panel */}
      {renderLivePreview()}
    </div>
  );

  return (
    <div className="bg-background-light dark:bg-background-dark font-display text-gray-900 dark:text-gray-100 h-screen flex overflow-hidden text-base">
      {/* Sidebar */}
      <aside className="w-80 flex-shrink-0 bg-white dark:bg-surface-dark border-r border-gray-300 dark:border-gray-800 flex flex-col h-full z-30">
        {/* Logo Section */}
        <div className="p-8 pb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <span className="material-symbols-outlined filled text-3xl">health_and_safety</span>
            </div>
            <div>
              <h1 className="text-gray-900 dark:text-white text-lg font-bold leading-tight">Caregiver Portal</h1>
            </div>
          </div>
          <div className="h-px w-full bg-gray-200 dark:bg-gray-700"></div>
        </div>

        {/* Steps Navigation */}
        <nav className="flex-1 overflow-y-auto sidebar-scroll px-6 pb-6">
          <div className="flex flex-col gap-2">
            {/* Step 1: Profile Selection */}
            <div
              onClick={() => currentStep > 1 && setCurrentStep(1)}
              className={`group flex items-center gap-4 px-4 py-4 rounded-xl transition-colors ${
                currentStep === 1
                  ? 'py-5 bg-primary/10 border-l-8 border-primary shadow-sm cursor-default'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer opacity-70'
              }`}
            >
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-lg ${
                  currentStep === 1
                    ? 'bg-primary text-white'
                    : 'bg-green-600 text-white'
                }`}
              >
                {currentStep === 1 ? '1' : <span className="material-symbols-outlined text-lg font-bold">check</span>}
              </span>
              <p className={`text-lg ${currentStep === 1 ? 'font-bold text-gray-900 dark:text-white' : 'font-medium text-gray-600 dark:text-gray-400'}`}>
                Profile Selection
              </p>
            </div>

            <div className={`ml-8 h-8 w-1 ${currentStep > 1 ? 'bg-green-600/30' : 'bg-gray-300 dark:bg-gray-700'} my-1 rounded-full`}></div>

            {/* Step 2: User Details */}
            <div
              onClick={() => currentStep > 2 && setCurrentStep(2)}
              className={`group flex items-center gap-4 px-4 py-4 rounded-xl transition-colors ${
                currentStep === 2
                  ? 'py-5 bg-primary/10 border-l-8 border-primary shadow-sm cursor-default'
                  : currentStep > 2
                    ? 'hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer opacity-70'
                    : 'opacity-60 cursor-not-allowed'
              }`}
            >
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-lg ${
                  currentStep === 2
                    ? 'bg-primary text-white'
                    : currentStep > 2
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                {currentStep > 2 ? <span className="material-symbols-outlined text-lg font-bold">check</span> : '2'}
              </span>
              <p className={`text-lg ${currentStep === 2 ? 'font-bold text-gray-900 dark:text-white' : 'font-medium text-gray-600 dark:text-gray-400'}`}>
                User Details
              </p>
            </div>

            <div className={`ml-8 h-8 w-1 ${currentStep > 2 ? 'bg-green-600/30' : 'bg-gray-300 dark:bg-gray-700'} my-1 rounded-full`}></div>

            {/* Step 3: Configuration & Deployment */}
            <div
              className={`group flex items-center gap-4 px-4 py-4 rounded-xl transition-colors ${
                currentStep === 3
                  ? 'py-5 bg-primary/10 border-l-8 border-primary shadow-sm cursor-default'
                  : 'opacity-60 cursor-not-allowed'
              }`}
            >
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-lg ${
                  currentStep === 3
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                3
              </span>
              <div className="flex flex-col">
                <span className={`text-lg ${currentStep === 3 ? 'font-bold text-gray-900 dark:text-white' : 'font-medium text-gray-600 dark:text-gray-400'}`}>
                  Configuration & Deployment
                </span>
              </div>
            </div>
          </div>
        </nav>

        {/* User Profile */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-800">
          <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gray-300 dark:bg-gray-600 bg-center bg-cover border-2 border-white dark:border-gray-700 flex items-center justify-center">
              <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">person</span>
            </div>
            <div className="flex flex-col">
              <p className="text-base font-bold text-gray-900 dark:text-white">Sarah Jenkins</p>
              <button className="text-sm text-gray-600 dark:text-gray-400 underline hover:text-primary text-left">
                Log out
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full overflow-hidden relative bg-white dark:bg-background-dark">
        {currentStep === 1 ? (
          <>
            <div className="flex-1 overflow-y-auto p-8 lg:p-12">
              {renderProfileSelection()}
            </div>

            {/* Bottom Action Bar for Step 1 */}
            <div className="flex-shrink-0 bg-white dark:bg-surface-dark border-t-2 border-gray-200 dark:border-gray-800 p-6 px-8 lg:px-12 shadow-[0_-8px_30px_rgba(0,0,0,0.05)]">
              <div className="max-w-6xl mx-auto flex items-center justify-between">
                <button
                  onClick={handleGoBack}
                  className="flex items-center justify-center h-16 px-8 rounded-xl text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-bold text-xl transition-colors border-2 border-gray-200 hover:border-gray-400 dark:border-gray-700 dark:hover:border-gray-500 bg-white dark:bg-surface-dark"
                >
                  <span className="material-symbols-outlined mr-3 text-2xl font-bold">arrow_back</span>
                  Go Back
                </button>
                <button
                  onClick={handleNextStep}
                  className="flex items-center justify-center h-16 px-12 bg-primary hover:bg-primary/90 text-white font-bold text-xl rounded-xl shadow-lg shadow-primary/20 transition-all transform hover:-translate-y-1 active:translate-y-0 focus:ring-4 ring-primary/30"
                >
                  Next Step
                  <span className="material-symbols-outlined ml-3 text-2xl font-bold">arrow_forward</span>
                </button>
              </div>
            </div>
          </>
        ) : currentStep === 2 ? (
          <>
            <div className="flex-1 overflow-y-auto p-8 lg:p-12">
              {renderUserDetails()}
            </div>

            {/* Bottom Action Bar for Step 2 */}
            <div className="flex-shrink-0 bg-white dark:bg-surface-dark border-t-2 border-gray-200 dark:border-gray-800 p-6 px-8 lg:px-12 shadow-[0_-8px_30px_rgba(0,0,0,0.05)]">
              <div className="max-w-2xl mx-auto flex items-center justify-between">
                <button
                  onClick={handleGoBack}
                  className="flex items-center justify-center h-16 px-8 rounded-xl text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-bold text-xl transition-colors border-2 border-gray-200 hover:border-gray-400 dark:border-gray-700 dark:hover:border-gray-500 bg-white dark:bg-surface-dark"
                >
                  <span className="material-symbols-outlined mr-3 text-2xl font-bold">arrow_back</span>
                  Go Back
                </button>
                <button
                  onClick={handleNextStep}
                  className="flex items-center justify-center h-16 px-12 bg-primary hover:bg-primary/90 text-white font-bold text-xl rounded-xl shadow-lg shadow-primary/20 transition-all transform hover:-translate-y-1 active:translate-y-0 focus:ring-4 ring-primary/30"
                >
                  Next Step
                  <span className="material-symbols-outlined ml-3 text-2xl font-bold">arrow_forward</span>
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {renderConfiguration()}

            {/* Bottom Action Bar for Step 3 - Go Back only */}
            <div className="flex-shrink-0 bg-white dark:bg-surface-dark border-t-2 border-gray-200 dark:border-gray-800 p-6 px-8 lg:px-12 shadow-[0_-8px_30px_rgba(0,0,0,0.05)] xl:w-[calc(100%-480px)] w-full">
              <div className="max-w-2xl mx-auto flex items-center">
                <button
                  onClick={handleGoBack}
                  className="flex items-center justify-center h-16 px-8 rounded-xl text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white font-bold text-xl transition-colors border-2 border-gray-200 hover:border-gray-400 dark:border-gray-700 dark:hover:border-gray-500 bg-white dark:bg-surface-dark"
                >
                  <span className="material-symbols-outlined mr-3 text-2xl font-bold">arrow_back</span>
                  Go Back
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* COMMENTED OUT - Deploy Modal
      {showDeployModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-white text-3xl">devices</span>
                  <h2 className="text-xl font-bold text-white">Deploy to Device</h2>
                </div>
                <button
                  onClick={() => { setShowDeployModal(false); setDeployError(''); }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <span className="material-symbols-outlined text-2xl">close</span>
                </button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Enter the Device ID to deploy the <strong>{getTemplateId()}</strong> configuration.
              </p>
              <div className="mb-6">
                <label htmlFor="deviceId" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Device ID
                </label>
                <input
                  type="text"
                  id="deviceId"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  placeholder="e.g., device-123-abc"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Find this ID in the AidFone app settings on the Android device.
                </p>
              </div>
              {deployError && (
                <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                    <span className="material-symbols-outlined text-xl">error</span>
                    <p className="font-medium">{deployError}</p>
                  </div>
                </div>
              )}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-6">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Configuration Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Profile:</span>
                    <span className="font-medium text-gray-900 dark:text-white capitalize">{selectedProfile}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Template:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{getTemplateId()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Monthly Cost:</span>
                    <span className="font-bold text-primary">${calculateMonthlyCost()}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowDeployModal(false); setDeployError(''); }}
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={isDeploying}
                  className="flex-1 px-6 py-3 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isDeploying ? (
                    <>
                      <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
                      Deploying...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-xl">rocket_launch</span>
                      Deploy Now
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      */}

      {/* Success Modal */}
      {showSuccessModal && successData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-[fadeIn_0.3s_ease-out]">
          <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-[slideUp_0.4s_ease-out]">
            {/* Success Header with Animation */}
            <div className="relative bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 px-8 py-10 text-center overflow-hidden">
              {/* Animated Background Circles */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 rounded-full animate-pulse"></div>
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-white/10 rounded-full animate-pulse delay-150"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full"></div>
              </div>

              {/* Success Checkmark */}
              <div className="relative">
                <div className="mx-auto w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg shadow-emerald-600/30 animate-[scaleIn_0.5s_ease-out]">
                  <span className="material-symbols-outlined text-emerald-500 text-5xl animate-[checkmark_0.6s_ease-out_0.2s_both]">check_circle</span>
                </div>
                <h2 className="mt-6 text-2xl font-bold text-white tracking-tight">Configuration Deployed!</h2>
                <p className="mt-2 text-white/80 text-sm">Your device is now configured and ready to use</p>
              </div>
            </div>

            {/* Content */}
            <div className="px-8 py-6 space-y-5">
              {/* Device Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-md">
                  <span className="material-symbols-outlined text-white text-2xl">smartphone</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Device ID</p>
                  <p className="text-gray-900 dark:text-white font-mono text-sm truncate">{successData.deviceId}</p>
                </div>
              </div>

              {/* Profile & Template */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`material-symbols-outlined text-lg ${
                      profiles.find(p => p.id === successData.profile)?.iconColor || 'text-gray-500'
                    }`}>
                      {profiles.find(p => p.id === successData.profile)?.icon || 'person'}
                    </span>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Profile</p>
                  </div>
                  <p className="text-gray-900 dark:text-white font-semibold capitalize">{successData.profile}</p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-lg text-purple-500">dashboard</span>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Template</p>
                  </div>
                  <p className="text-gray-900 dark:text-white font-semibold text-sm">{successData.templateId.replace(/_/g, ' ')}</p>
                </div>
              </div>

              {/* Features */}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Enabled Features</p>
                <div className="flex flex-wrap gap-2">
                  {successData.features.map((featureId) => {
                    const feature = [...initialBasicFeatures, ...initialPremiumFeatures].find(f => f.id === featureId);
                    return (
                      <span
                        key={featureId}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30 text-teal-700 dark:text-teal-300 text-xs font-medium rounded-full border border-teal-200 dark:border-teal-700"
                      >
                        <span className="material-symbols-outlined text-sm">check</span>
                        {feature?.name || featureId}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Monthly Cost */}
              <div className="flex items-center justify-between p-5 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl border border-emerald-200 dark:border-emerald-800">
                <div>
                  <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Monthly Subscription</p>
                  <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">Billed monthly, cancel anytime</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400">${successData.monthlyCost}</p>
                  <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">per month</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 pb-8">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setSuccessData(null);
                }}
                className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-bold text-lg rounded-2xl shadow-lg shadow-teal-500/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined">done_all</span>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Keyframe Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes scaleIn {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
        @keyframes checkmark {
          0% { opacity: 0; transform: scale(0) rotate(-45deg); }
          50% { opacity: 1; transform: scale(1.2) rotate(0deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
      `}</style>
    </div>
  );
};

export default CaregiverPortal;
